import { z } from 'zod';
import { uuidSchema } from './common.js';
/** `YYYY-MM-DD` date with bounds check delegated to the regex shape. */
const isoDateSchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'YYYY-MM-DD');
/**
 * Wave 2.64 BE (TASK-231). Wire schemas for the
 * `GET /admin/analytics/revenue-split` endpoint.
 *
 * The endpoint ranks presets by total seconds played across all venues
 * within `[from, to]` and joins them to the author selector — used by
 * the admin dashboard "Revenue split" panel (admin-prd §10.2).
 *
 * Note on `estimated_revenue_share_kop`: the value is a coarse pro-rata
 * estimate produced by `total_seconds × DEFAULT_KOP_PER_SECOND` (see
 * `RevenueSplitService.KOP_PER_SECOND`). A precise per-period rate would
 * require pulling the selector's contractual share alongside the actual
 * paid payouts; until that contract surface lands the placeholder lets
 * the FE render a magnitude without misrepresenting a settled amount.
 */
export const revenueSplitQuerySchema = z
    .object({
    from: isoDateSchema,
    to: isoDateSchema,
    limit: z.coerce.number().int().min(1).max(100).default(20),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.from > v.to) {
        ctx.addIssue({
            code: 'custom',
            path: ['from'],
            message: 'from must be <= to',
        });
    }
});
export const revenueSplitRowSchema = z.object({
    preset_id: uuidSchema,
    preset_name: z.string().nullable(),
    author_user_id: uuidSchema.nullable(),
    author_display_name: z.string().nullable(),
    total_seconds: z.number().int().nonnegative(),
    estimated_revenue_share_kop: z.number().int().nonnegative(),
});
export const revenueSplitResponseSchema = z.object({
    from: isoDateSchema,
    to: isoDateSchema,
    items: z.array(revenueSplitRowSchema),
    /**
     * Sum of `total_seconds` across the entire window (not just the
     * returned top-N) so the FE can compute a "% of total" badge per row.
     */
    total_seconds_all_presets: z.number().int().nonnegative(),
});
//# sourceMappingURL=revenue-split.js.map